import {Box} from '@primer/react-brand'

import {AutoPlayVideoButton} from '../../AutoPlayVideo/AutoPlayVideoButton'
import {useAutoPlayVideo} from '../../AutoPlayVideo/useAutoPlayVideo'

import styles from './ContentfulAnimatedVideo.module.css'
import type {AnimatedVideo} from '../../../schemas/contentful/contentTypes/animatedVideo'

export type AnimatedVideoProps = {
  component: AnimatedVideo
  analyticsLocation?: string
}

export function ContentfulAnimatedVideo({component, analyticsLocation}: AnimatedVideoProps) {
  const {video, playLabel, pauseLabel, replayLabel} = component.fields
  const description = video.fields?.description || ''

  const {
    registerVideo,
    buttonLabel,
    buttonPressed,
    buttonAriaLabel,
    buttonAnalyticsAttributes,
    toggle,
    onPlay,
    onPause,
    onEnded,
  } = useAutoPlayVideo({
    copy: {
      label: {play: 'Play', pause: 'Pause', replay: 'Replay'},
      ariaLabel: {play: playLabel, pause: pauseLabel, replay: replayLabel},
    },
    intersectionBehavior: 'continuous',
    intersectionObserverOptions: {threshold: 0.5, rootMargin: '0px'},
    analytics: {context: description, location: analyticsLocation},
  })

  return (
    <div className="position-relative width-full height-full">
      <Box role="img" className={styles.animatedVideoContainer} aria-label={description}>
        <video
          playsInline
          muted
          className={styles.animatedVideoPlayer}
          ref={registerVideo}
          onPlay={onPlay}
          onPause={onPause}
          onEnded={onEnded}
          data-testid="animated-video"
        >
          <source src={video.fields.file.url} type="video/mp4; codecs=avc1.4d002a" />
        </video>
      </Box>

      <AutoPlayVideoButton
        className={styles.animatedVideoPlayerButton}
        iconClassName={styles.playButtonIcon}
        pressed={buttonPressed}
        label={buttonLabel}
        ariaLabel={buttonAriaLabel}
        onClick={toggle}
        analyticsAttributes={buttonAnalyticsAttributes}
      />
    </div>
  )
}
